/**
 * usePlacementEngine hook
 *
 * Orchestrates the skill suggestion acceptance flow:
 * 1. Fetches all subscribed + custom packages and flattens to PackageSkillEntry[]
 * 2. Calls findSkillPlacement to determine match type
 * 3. For exact matches: auto-creates/updates SkillRating, marks suggestion accepted
 * 4. For fuzzy matches: exposes state for PlacementConfirmationDialog
 * 5. For no match: exposes state for PackageCreationDialog
 *
 * Requirements: 1.1, 2.1, 2.2, 2.3, 3.2, 3.3, 4.3, 4.4, 4.5, 5.1, 5.2, 5.3
 */

import { useState, useCallback } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from "@backend-schema";
import type { PendingSuggestion } from '../types/suggestions';
import type { PlacementResult, PackageSkillEntry } from '../types/placement';
import { findSkillPlacement } from '../utils/skill-matcher';
import { parsePayload, validateSkillRatingPayload } from '../utils/suggestion-payload';

const client = generateClient<Schema>();

interface ParsedSuggestionData {
  skillName: string;
  skillKey: string;
  rating: number;
}

export interface UsePlacementEngineReturn {
  placementResult: PlacementResult | null;
  pendingSkillName: string;
  isSearching: boolean;
  searchStatus: string;
  error: string | null;
  allPackageSkills: PackageSkillEntry[];
  startPlacement: (suggestion: PendingSuggestion) => Promise<void>;
  confirmPlacement: () => Promise<void>;
  rejectPlacement: () => void;
  dismissCreation: () => void;
  createPackageAndPlace: (packageName: string, categoryName: string) => Promise<void>;
  placeInExistingCategory: (categoryId: string, packageId: string, packageName: string, categoryName: string) => Promise<void>;
}

// ─── Package fetching helpers ─────────────────────────────────────────────────

async function fetchCuratedPackageSkills(): Promise<PackageSkillEntry[]> {
  const entries: PackageSkillEntry[] = [];

  const userPkgs = await client.models.UserSkillPackages.list();
  const userPkgRecords = userPkgs.data ?? [];

  for (const userPkg of userPkgRecords) {
    const pkgId = userPkg.skillPackageId;

    const pkgResult = await client.models.SkillPackages.get({ id: pkgId });
    const pkg = pkgResult.data;
    if (!pkg) continue;

    const categoriesResult = await client.models.SkillCategories.list({
      filter: { skillPackageId: { eq: pkgId } },
    });
    const categories = categoriesResult.data ?? [];

    for (const category of categories) {
      const skillsResult = await client.models.Skills.list({
        filter: { categoryId: { eq: category.id || '' } },
      });
      const skills = skillsResult.data ?? [];

      for (const skill of skills) {
        entries.push({
          skillId: skill.id || '',
          skillKey: (skill.skillKey as string | null) ?? null,
          skillName: skill.name as string,
          packageName: pkg.name as string,
          packageId: pkgId,
          categoryName: category.name as string,
          categoryId: category.id || '',
          source: 'curated',
        });
      }
    }
  }

  return entries;
}

async function fetchCustomPackageSkills(): Promise<PackageSkillEntry[]> {
  const entries: PackageSkillEntry[] = [];

  const customPkgs = await client.models.CustomSkillPackages.list();
  const customPkgRecords = customPkgs.data ?? [];

  for (const pkg of customPkgRecords) {
    const categoriesResult = await client.models.CustomSkillCategories.list({
      filter: { customPackageId: { eq: pkg.id || '' } },
    });
    const categories = categoriesResult.data ?? [];

    for (const category of categories) {
      const skillsResult = await client.models.CustomSkills.list({
        filter: { customCategoryId: { eq: category.id || '' } },
      });
      const skills = skillsResult.data ?? [];

      for (const skill of skills) {
        entries.push({
          skillId: skill.existingSkillId ?? (skill.id || ''),
          skillKey: null,
          skillName: skill.name as string,
          packageName: pkg.name as string,
          packageId: pkg.id || '',
          categoryName: category.name as string,
          categoryId: category.id || '',
          source: 'custom',
        });
      }
    }
  }

  return entries;
}

async function fetchAllPackageSkills(): Promise<PackageSkillEntry[]> {
  const [curated, custom] = await Promise.all([
    fetchCuratedPackageSkills(),
    fetchCustomPackageSkills(),
  ]);
  return [...curated, ...custom];
}

// ─── Mutation helpers ─────────────────────────────────────────────────────────

async function createOrUpdateSkillRating(
  skillId: string,
  skillKey: string,
  rating: number,
): Promise<void> {
  const existing = await client.models.SkillRating.list({
    filter: { skillKey: { eq: skillKey } },
  });

  const existingItem = existing.data?.[0];
  if (existingItem) {
    const result = await client.models.SkillRating.update({
      id: existingItem.id,
      rating,
      lastModified: new Date().toISOString(),
      modifiedBy: 'user',
    });
    if (result.errors?.length) {
      throw new Error(result.errors.map((e) => e.message).join(', '));
    }
  } else {
    const result = await client.models.SkillRating.create({
      skillId,
      skillKey,
      rating,
      lastModified: new Date().toISOString(),
      modifiedBy: 'user',
    });
    if (result.errors?.length) {
      throw new Error(result.errors.map((e) => e.message).join(', '));
    }
  }
}

async function markSuggestionAccepted(suggestionId: string): Promise<void> {
  const result = await client.models.PendingSuggestion.update({
    id: suggestionId,
    status: 'accepted',
  });
  if (result.errors?.length) {
    throw new Error(result.errors.map((e) => e.message).join(', '));
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function usePlacementEngine(): UsePlacementEngineReturn {
  const [placementResult, setPlacementResult] = useState<PlacementResult | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchStatus, setSearchStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [currentSuggestion, setCurrentSuggestion] = useState<PendingSuggestion | null>(null);
  const [parsedData, setParsedData] = useState<ParsedSuggestionData | null>(null);
  const [allPackageSkills, setAllPackageSkills] = useState<PackageSkillEntry[]>([]);

  const startPlacement = useCallback(async (suggestion: PendingSuggestion) => {
    setIsSearching(true);
    setError(null);
    setPlacementResult(null);
    setCurrentSuggestion(suggestion);
    setSearchStatus('Checking your subscribed packages...');

    try {
      const payload = parsePayload(suggestion.payload);

      if (!validateSkillRatingPayload(payload)) {
        throw new Error('Invalid skill rating payload');
      }

      const { skillName, skillKey, rating } = payload;
      setParsedData({ skillName, skillKey, rating });

      setSearchStatus('Looking for an exact match in your packages...');
      const packageSkills = await fetchAllPackageSkills();

      setSearchStatus(packageSkills.length > 0
        ? `Searching ${packageSkills.length} skills across your packages...`
        : 'No packages found — will create a new one for you...'
      );
      setAllPackageSkills(packageSkills);
      const result = findSkillPlacement(skillName, skillKey, packageSkills);

      if (result.matchType === 'exact') {
        setSearchStatus('Match found — saving your rating...');
        await createOrUpdateSkillRating(result.skillId!, result.skillKey ?? skillKey, rating);
        await markSuggestionAccepted(suggestion.id);
        setPlacementResult(result);
        setSearchStatus('');
      } else {
        // fuzzy, category, or none — show the placement picker
        setSearchStatus('');
        setPlacementResult(result);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred during placement';
      setError(message);
      setSearchStatus('');
    } finally {
      setIsSearching(false);
    }
  }, []);

  const confirmPlacement = useCallback(async () => {
    if (!placementResult || !currentSuggestion || !parsedData) return;

    setError(null);
    try {
      await createOrUpdateSkillRating(
        placementResult.skillId!,
        placementResult.skillKey ?? parsedData.skillKey,
        parsedData.rating,
      );
      await markSuggestionAccepted(currentSuggestion.id);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to confirm placement';
      setError(message);
      console.error(`[usePlacementEngine] Confirm error for suggestion ${currentSuggestion.id}:`, err);
    }
  }, [placementResult, currentSuggestion, parsedData]);

  const rejectPlacement = useCallback(() => {
    // Switch from fuzzy to 'none' so PackageCreationDialog shows
    setPlacementResult({ matchType: 'none' });
    setError(null);
  }, []);

  const dismissCreation = useCallback(() => {
    // Keep suggestion pending, clear placement state
    setPlacementResult(null);
    setCurrentSuggestion(null);
    setParsedData(null);
    setError(null);
  }, []);

  const createPackageAndPlace = useCallback(async (packageName: string, categoryName: string) => {
    if (!currentSuggestion || !parsedData) return;

    setError(null);
    try {
      // Create CustomSkillPackages
      const pkgResult = await client.models.CustomSkillPackages.create({ name: packageName });
      if (pkgResult.errors?.length) {
        throw new Error(pkgResult.errors.map((e) => e.message).join(', '));
      }
      const pkgId = pkgResult.data?.id;
      if (!pkgId) throw new Error('Failed to create custom package — no ID returned');

      // Create CustomSkillCategories
      const catResult = await client.models.CustomSkillCategories.create({
        name: categoryName,
        customPackageId: pkgId,
      });
      if (catResult.errors?.length) {
        throw new Error(catResult.errors.map((e) => e.message).join(', '));
      }
      const catId = catResult.data?.id;
      if (!catId) throw new Error('Failed to create custom category — no ID returned');

      // Create CustomSkills
      const skillResult = await client.models.CustomSkills.create({
        name: parsedData.skillName,
        customCategoryId: catId,
      });
      if (skillResult.errors?.length) {
        throw new Error(skillResult.errors.map((e) => e.message).join(', '));
      }
      const skillId = skillResult.data?.id;
      if (!skillId) throw new Error('Failed to create custom skill — no ID returned');

      // Create SkillRating
      await createOrUpdateSkillRating(skillId, parsedData.skillKey, parsedData.rating);

      // Mark suggestion accepted
      await markSuggestionAccepted(currentSuggestion.id);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create package and place skill';
      setError(message);
      console.error(`[usePlacementEngine] Create package error for suggestion ${currentSuggestion.id}:`, err);
    }
  }, [currentSuggestion, parsedData]);

  // Place skill into an existing category (user selected from picker)
  const placeInExistingCategory = useCallback(async (
    categoryId: string,
    _packageId: string,
    packageName: string,
    categoryName: string,
  ) => {
    if (!currentSuggestion || !parsedData) return;
    setError(null);
    try {
      let resolvedCategoryId = categoryId;

      // Handle new category creation in an existing custom package
      if (categoryId.startsWith('__new__:')) {
        const newCatName = categoryId.replace('__new__:', '');
        const catResult = await client.models.CustomSkillCategories.create({
          name: newCatName,
          customPackageId: _packageId,
        });
        if (catResult.errors?.length) {
          throw new Error(catResult.errors.map((e) => e.message).join(', '));
        }
        resolvedCategoryId = catResult.data?.id ?? '';
        if (!resolvedCategoryId) throw new Error('Failed to create category — no ID returned');
      }

      // Create a CustomSkill in the selected/new category
      const skillResult = await client.models.CustomSkills.create({
        name: parsedData.skillName,
        customCategoryId: resolvedCategoryId,
      });
      if (skillResult.errors?.length) {
        throw new Error(skillResult.errors.map((e) => e.message).join(', '));
      }
      const skillId = skillResult.data?.id;
      if (!skillId) throw new Error('Failed to create skill — no ID returned');

      await createOrUpdateSkillRating(skillId, parsedData.skillKey, parsedData.rating);
      await markSuggestionAccepted(currentSuggestion.id);

      setPlacementResult({
        matchType: 'exact',
        packageName,
        categoryName,
        source: 'custom',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to place skill';
      setError(message);
    }
  }, [currentSuggestion, parsedData]);

  return {
    placementResult,
    pendingSkillName: parsedData?.skillName ?? '',
    isSearching,
    searchStatus,
    error,
    allPackageSkills,
    startPlacement,
    confirmPlacement,
    rejectPlacement,
    dismissCreation,
    createPackageAndPlace,
    placeInExistingCategory,
  };
}
